import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Flame, Lightbulb, Users2, Scale, Trophy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatError, t } from "@/lib/api";
import { PageHeader } from "@/components/Common";

function Pulse() {
  const [current, setCurrent] = useState(null);
  const [vals, setVals] = useState({ autonomy: 0, meaning: 0 });
  useEffect(() => { api.get("/pulse/me").then(({ data }) => setCurrent(data)); }, []);
  const send = async () => {
    try { await api.post("/pulse", vals); setCurrent(vals); toast.success(t("Merci ! Votre ressenti est enregistré (+5 pts)", "Thanks! Your feedback has been saved (+5 pts)")); }
    catch (e) { toast.error(formatError(e)); }
  };
  const Row = ({ k, label }) => (
    <div>
      <div className="text-sm text-slate-600">{label}</div>
      <div className="mt-2 flex gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`pulse-${k}-${n}`} onClick={() => setVals({ ...vals, [k]: n })}
            className={`h-9 w-9 rounded-lg border text-sm font-semibold transition-colors ${vals[k] >= n ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-200 hover:border-emerald-300"}`}>{n}</button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="rounded-3xl border border-emerald-200 bg-emerald-50/60 p-6" data-testid="pulse-widget">
      <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700">{t("Météo de la semaine", "Weekly check-in")}</div>
      {current?.autonomy ? (
        <p className="mt-3 text-sm text-slate-600" data-testid="pulse-done">{t(`Merci ! Autonomie ${current.autonomy}/5 · Sens ${current.meaning}/5. Rendez-vous la semaine prochaine.`, `Thanks! Autonomy ${current.autonomy}/5 · Meaning ${current.meaning}/5. See you next week.`)}</p>
      ) : (
        <div className="mt-4 space-y-4">
          <Row k="autonomy" label={t("Je me sens autonome dans mon travail", "I feel autonomous in my work")} />
          <Row k="meaning" label={t("Mes idées ont un impact réel", "My ideas have a real impact")} />
          <button data-testid="pulse-submit-button" disabled={!vals.autonomy || !vals.meaning} onClick={send} className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white disabled:opacity-40 hover:bg-emerald-700 transition-colors">{t("Envoyer", "Send")}</button>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [ws, setWs] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [board, setBoard] = useState([]);
  useEffect(() => {
    api.get("/workshops").then(({ data }) => setWs(data));
    api.get("/ideas", { params: { sort: "votes" } }).then(({ data }) => setIdeas(data.slice(0, 3)));
    api.get("/leaderboard").then(({ data }) => setBoard(data));
  }, []);

  const quick = [
    { to: "/app/simulateur", icon: Users2, label: t("Simuler une perspective", "Simulate a perspective"), cls: "bg-white" },
    { to: "/app/idees", icon: Lightbulb, label: t("Proposer une micro-idée", "Suggest a micro-idea"), cls: "bg-white" },
    { to: "/app/matrice", icon: Scale, label: t("Tester la solidité d'une idée", "Stress-test an idea"), cls: "bg-white" },
  ];

  return (
    <div>
      <PageHeader eyebrow={t("Tableau de bord", "Dashboard")} title={`${t("Bonjour", "Hello")} ${user.name.split(" ")[0]}`} subtitle={t("Quelques minutes d'entraînement par jour suffisent à débloquer la routine de réflexion.", "A few minutes of training a day is enough to break out of routine thinking.")} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {ws && (
          <Link to={`/app/ateliers/${ws.challenge.workshop_id}`} data-testid="weekly-challenge-card" className="lift md:col-span-2 rounded-3xl bg-slate-900 p-8 text-white fade-up">
            <div className="flex items-center gap-2 text-amber-400 text-xs font-semibold uppercase tracking-wider"><Flame className="h-4 w-4" /> {t("Défi de la semaine", "Weekly challenge")} · {t("S", "W")}{ws.challenge.week}</div>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl font-semibold leading-tight">{ws.challenge.title}</h2>
            <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-semibold text-slate-900">{t("Lancer l'atelier", "Start workshop")} {ws.challenge.workshop} <ArrowRight className="h-4 w-4" /></div>
          </Link>
        )}
        <Pulse />
        {quick.map(({ to, icon: Icon, label, cls }) => (
          <Link key={to} to={to} data-testid={`quick-link-${to.split("/").pop()}`} className={`lift group rounded-3xl border border-slate-200 p-6 ${cls}`}>
            <Icon className="h-6 w-6 text-indigo-600" />
            <div className="mt-6 flex items-center justify-between font-display text-lg font-semibold">{label}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></div>
          </Link>
        ))}
        <div className="md:col-span-2 rounded-3xl border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between"><h3 className="font-display text-lg font-semibold">{t("Idées les plus soutenues", "Most supported ideas")}</h3><Link to="/app/idees" className="text-sm text-indigo-600">{t("Tout voir", "See all")}</Link></div>
          <div className="mt-4 divide-y divide-slate-100">
            {ideas.map((i) => (
              <Link key={i.idea_id} to={`/app/idees/${i.idea_id}`} className="flex items-center justify-between py-3 hover:text-indigo-600 transition-colors" data-testid={`dashboard-idea-${i.idea_id}`}>
                <span className="text-sm font-medium">{i.title}</span><span className="font-mono text-xs text-slate-500">{i.vote_count} {t("votes", "votes")}</span>
              </Link>
            ))}
          </div>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="leaderboard">
          <h3 className="flex items-center gap-2 font-display text-lg font-semibold"><Trophy className="h-5 w-5 text-amber-500" /> {t("Les plus audacieux", "Boldest thinkers")}</h3>
          <ol className="mt-4 space-y-2.5">
            {board.map((b, n) => (
              <li key={b.user_id} className="flex items-center gap-3 text-sm">
                <span className="font-mono w-5 text-slate-400">{n + 1}</span>
                <span className="flex-1 truncate font-medium">{b.name}</span>
                <span className="font-mono text-xs text-amber-600">{b.points}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
